// ─── Search Form Renderer ───────────────────────────────────────────────────
//
// One component for all search ad formats. Reads config.sections[] to
// determine which sections to render and in what order.

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Plus, ImagePlus, AlertCircle, Trash2, Eye, X, ImageIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { GearExchangeModal, type GXImportedFile } from '../gear-exchange/GearExchangeModal';
import { GearExchangeImportButton } from '../gear-exchange/GearExchangeImportButton';
import { OfferLedSection } from '../ad-details/OfferLedSection';
import { fetchRsaDefaults, type RsaDefaultImageSet } from './rsaDefaults';
import { fetchDefaultMedia, type DefaultMediaMap } from './defaultMedia';
import {
  DynamicTextFieldArray,
  SearchCTADropdown,
  FieldLabel,
  inputClass,
  issueBorder,
  InlineIssue,
  MediaSlotSection,
  YouTubeVideoCard,
  SitelinksSection,
  type SlotConfig,
  type SearchFormState,
  type MediaFileItem,
  type ImageExtension,
  type FieldIssue,
} from './shared';
import type { SearchFormatConfig, SearchSection, MediaSlotConfig } from './search-config';
import type { Campaign } from '@/api/campaigns';
import type { GXMultiSearchResponse } from '@/api/gearExchange';

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Count errors in fieldIssues for specific keys. */
function countIssuesForKeys(
  fieldIssues: Record<string, FieldIssue>,
  keys: string[],
): { errors: number; warnings: number } {
  let errors = 0;
  let warnings = 0;
  for (const key of keys) {
    const issue = fieldIssues[key];
    if (issue) {
      errors += issue.errors.length;
      warnings += issue.warnings.length;
    }
  }
  return { errors, warnings };
}

/** Count errors in fieldIssues matching a key prefix. */
function countIssuesForPrefix(
  fieldIssues: Record<string, FieldIssue>,
  prefix: string,
): { errors: number; warnings: number } {
  let errors = 0;
  let warnings = 0;
  for (const [key, issue] of Object.entries(fieldIssues)) {
    if (key.startsWith(prefix)) {
      errors += issue.errors.length;
      warnings += issue.warnings.length;
    }
  }
  return { errors, warnings };
}

/** Count slot-level issues AND per-file ratio issues for files in the given slots. */
function countMediaIssues(
  fieldIssues: Record<string, FieldIssue>,
  slotKeys: string[],
  files: MediaFileItem[],
): { errors: number; warnings: number } {
  const base = countIssuesForKeys(
    fieldIssues,
    slotKeys.map((k) => `media_${k}`),
  );
  const fileIds = new Set(files.filter((f) => slotKeys.includes(f.slot)).map((f) => f.id));
  for (const [key, issue] of Object.entries(fieldIssues)) {
    if (!key.startsWith('media_ratio_')) continue;
    const fileId = key.slice('media_ratio_'.length);
    if (fileIds.has(fileId)) {
      base.errors += issue.errors.length;
      base.warnings += issue.warnings.length;
    }
  }
  return base;
}

/** Small error/warning badge shown on collapsed accordion headers. */
function CollapsedIssueBadge({
  errors,
  warnings,
  isOpen,
}: {
  errors: number;
  warnings: number;
  isOpen: boolean;
}) {
  if (isOpen || (errors === 0 && warnings === 0)) return null;
  return (
    <>
      {errors > 0 && (
        <span className="inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 text-[10px] font-medium">
          <AlertCircle className="w-3 h-3" />
          {errors}
        </span>
      )}
      {warnings > 0 && (
        <span
          className={`inline-flex items-center gap-1 ${errors > 0 ? 'ml-1' : 'ml-2'} px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-600 text-[10px] font-medium`}
        >
          <AlertCircle className="w-3 h-3" />
          {warnings}
        </span>
      )}
    </>
  );
}

function mediaSlotToSlotConfig(ms: MediaSlotConfig): SlotConfig {
  return {
    slot: ms.slot,
    label: ms.label,
    range: ms.min === ms.max ? `${ms.min}` : `${ms.min}–${ms.max}`,
    dimensions: ms.dimensions,
    optional: ms.optional ?? ms.min === 0,
    accept: ms.accept,
    recommended: ms.recommended,
    recommendedCount: ms.recommendedCount,
    minLength: ms.minLength,
    acceptTypes: ms.acceptTypes,
    maxFileSize: ms.maxFileSize,
    acceptedRatios: ms.acceptedRatios,
    max: ms.max,
  };
}

let nextMediaId = 1;
function makeMediaId(slot?: string) {
  return `gen-${slot ?? 'media'}-${Date.now()}-${nextMediaId++}`;
}

// ─── Props ──────────────────────────────────────────────────────────────────

interface GenericSearchDetailsProps {
  config: SearchFormatConfig;
  state: SearchFormState;
  onChange: (patch: Partial<SearchFormState>) => void;
  showErrors: boolean;
  showWarnings: boolean;
  fieldIssues: Record<string, FieldIssue>;
  readOnly?: boolean;
  mediaFiles: MediaFileItem[];
  onMediaFilesChange: (files: MediaFileItem[]) => void;
  onMediaFileDimensionsLoaded?: (id: string, width: number, height: number) => void;
  imageExtensions?: ImageExtension[];
  onImageExtensionsChange?: (extensions: ImageExtension[]) => void;
  gxData?: GXMultiSearchResponse | null;
  gxLoading?: boolean;
  gxError?: boolean;
  campaign?: Campaign | null;
  campaignId?: string;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function SearchFormRenderer({
  config,
  state,
  onChange,
  showErrors,
  showWarnings,
  fieldIssues,
  mediaFiles,
  onMediaFilesChange,
  onMediaFileDimensionsLoaded,
  imageExtensions = [],
  onImageExtensionsChange,
  gxData,
  gxLoading,
  gxError,
  readOnly,
  campaign,
  campaignId,
}: GenericSearchDetailsProps) {
  const [accordionState, setAccordionState] = useState<Record<string, boolean>>({});
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  // Env-aware default media (e.g. Ford square logo). The persisted gs:// path
  // must come from the backend so it targets the current environment's bucket.
  const [defaultMedia, setDefaultMedia] = useState<DefaultMediaMap | null>(null);
  useEffect(() => {
    let ignore = false;
    fetchDefaultMedia().then((result) => {
      if (!ignore) setDefaultMedia(result);
    });
    return () => {
      ignore = true;
    };
  }, []);

  const slots = config.mediaSlots ?? {};

  // ── Accordion helpers ───────────────────────────────────────────────────
  function isOpen(key: string) {
    return accordionState[key] ?? false;
  }
  function toggle(key: string) {
    setAccordionState((s) => ({ ...s, [key]: !s[key] }));
  }

  // ── Media file helpers ──────────────────────────────────────────────────
  function filesForSlot(slot: string) {
    return mediaFiles.filter((f) => f.slot === slot);
  }

  function addFiles(slot: string, files: File[]) {
    const newItems = files.map((f) => ({ id: makeMediaId(slot), slot, file: f, saved: null }));
    onMediaFilesChange([...mediaFiles, ...newItems]);
    newItems.forEach((item) => {
      if (!item.file) return;
      if (item.file.type.startsWith('video/')) {
        const video = document.createElement('video');
        const url = URL.createObjectURL(item.file);
        video.preload = 'metadata';
        video.muted = true;
        video.src = url;
        video.currentTime = 0.1;
        video.addEventListener('seeked', () => {
          onMediaFileDimensionsLoaded?.(item.id, video.videoWidth, video.videoHeight);
          URL.revokeObjectURL(url);
        });
        video.addEventListener('error', () => URL.revokeObjectURL(url));
        video.load();
      } else {
        const img = new Image();
        const url = URL.createObjectURL(item.file);
        img.onload = () => {
          onMediaFileDimensionsLoaded?.(item.id, img.naturalWidth, img.naturalHeight);
          URL.revokeObjectURL(url);
        };
        img.onerror = () => URL.revokeObjectURL(url);
        img.src = url;
      }
    });
  }

  function removeFile(id: string) {
    onMediaFilesChange(mediaFiles.filter((f) => f.id !== id));
    setPendingDeleteId(null);
  }

  function handleRemoveFile(id: string) {
    const item = mediaFiles.find((f) => f.id === id);
    if (item?.saved) setPendingDeleteId(id);
    else removeFile(id);
  }

  function addYouTubeUrl() {
    onMediaFilesChange([
      ...mediaFiles,
      {
        id: makeMediaId('youtube_video'),
        slot: 'youtube_video',
        file: null,
        saved: null,
        url: '',
        expirationDate: '',
      },
    ]);
  }

  function updateYouTube(id: string, patch: { url?: string; expirationDate?: string }) {
    onMediaFilesChange(mediaFiles.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  }

  function handleDimensionsLoaded(id: string, width: number, height: number) {
    onMediaFileDimensionsLoaded?.(id, width, height);
  }

  // ── Section renderers ───────────────────────────────────────────────────

  function renderSection(section: SearchSection, index: number) {
    switch (section) {
      case 'about':
        return (
          <div key={index} className="border-l-2 border-[#0014cc]/30 pl-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
              About this format
            </p>
            <p className="text-sm text-slate-600 leading-relaxed">{config.aboutText}</p>
          </div>
        );

      case 'text':
        return (
          <fieldset key={index} disabled={readOnly} className="space-y-6">
            {config.headlines && (
              <DynamicTextFieldArray
                label={config.headlines.label}
                hint={config.headlines.hint}
                values={state.headlines}
                onChange={(v) => onChange({ headlines: v })}
                min={config.headlines.min}
                max={config.headlines.max}
                charLimit={config.headlines.charLimit}
                fieldIssuePrefix="headline"
                fieldIssues={fieldIssues}
                showErrors={showErrors}
                showWarnings={showWarnings}
                sectionIssue={fieldIssues['headlines']}
              />
            )}
            {config.longHeadlines && (
              <DynamicTextFieldArray
                label={config.longHeadlines.label}
                hint={config.longHeadlines.hint}
                values={state.longHeadlines}
                onChange={(v) => onChange({ longHeadlines: v })}
                min={config.longHeadlines.min}
                max={config.longHeadlines.max}
                charLimit={config.longHeadlines.charLimit}
                fieldIssuePrefix="long_headline"
                fieldIssues={fieldIssues}
                showErrors={showErrors}
                showWarnings={showWarnings}
                sectionIssue={fieldIssues['long_headlines']}
              />
            )}
            {config.descriptions && (
              <DynamicTextFieldArray
                label={config.descriptions.label}
                hint={config.descriptions.hint}
                values={state.descriptions}
                onChange={(v) => onChange({ descriptions: v })}
                min={config.descriptions.min}
                max={config.descriptions.max}
                charLimit={config.descriptions.charLimit}
                fieldIssuePrefix="description"
                fieldIssues={fieldIssues}
                showErrors={showErrors}
                showWarnings={showWarnings}
                sectionIssue={fieldIssues['descriptions']}
              />
            )}
            {config.businessName && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                    Business Name
                    {config.businessName.optional && (
                      <span className="normal-case font-normal tracking-normal text-slate-300 ml-1">
                        (optional)
                      </span>
                    )}
                  </p>
                  <span
                    className={`text-xs tabular-nums ${state.businessName.length > config.businessName.charLimit ? 'text-amber-600 font-medium' : 'text-slate-400'}`}
                  >
                    {state.businessName.length}/{config.businessName.charLimit}
                  </span>
                </div>
                <input
                  type="text"
                  value={state.businessName}
                  onChange={(e) => onChange({ businessName: e.target.value })}
                  placeholder="Ford"
                  className={`w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 transition-all ${
                    issueBorder(fieldIssues['business_name'], showErrors, showWarnings) ||
                    'border-slate-300 focus:ring-[#0014cc]/10 focus:border-[#0014cc]/50'
                  }`}
                />
                <InlineIssue
                  issue={fieldIssues['business_name']}
                  showErrors={showErrors}
                  showWarnings={showWarnings}
                />
              </div>
            )}
          </fieldset>
        );

      case 'display_url':
        if (!config.displayUrlPaths) return null;
        return (
          <fieldset key={index} disabled={readOnly}>
            <DynamicTextFieldArray
              label={config.displayUrlPaths.label}
              values={state.displayUrlPaths}
              onChange={(v) => onChange({ displayUrlPaths: v })}
              min={config.displayUrlPaths.min}
              max={config.displayUrlPaths.max}
              charLimit={config.displayUrlPaths.charLimit}
              fieldIssuePrefix="display_url_path"
              fieldIssues={fieldIssues}
              showErrors={showErrors}
              showWarnings={showWarnings}
              placeholder="path..."
              sectionIssue={fieldIssues['display_url_paths']}
            />
          </fieldset>
        );

      case 'images': {
        const imageSlotKeys = ['image', 'landscape_image', 'square_image', 'portrait_image'].filter(
          (k) => k in slots,
        );
        if (imageSlotKeys.length === 0) return null;
        const totalImageCount = mediaFiles.filter(
          (f) => imageSlotKeys.includes(f.slot) && (f.file || f.saved),
        ).length;
        const sharedMax = config.sharedImageMax;
        const sharedMaxIssue = fieldIssues['images_shared_max'];
        const imagesConditionalIssue = fieldIssues['images_conditional'];
        const badgeCounts = countMediaIssues(fieldIssues, imageSlotKeys, mediaFiles);
        if (sharedMaxIssue) {
          badgeCounts.errors += sharedMaxIssue.errors.length;
          badgeCounts.warnings += sharedMaxIssue.warnings.length;
        }
        if (imagesConditionalIssue) {
          badgeCounts.errors += imagesConditionalIssue.errors.length;
          badgeCounts.warnings += imagesConditionalIssue.warnings.length;
        }
        return (
          <div key={index}>
            <button
              type="button"
              onClick={() => toggle('images')}
              className="flex items-center justify-between w-full mb-1.5 group/sec"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 group-hover/sec:text-slate-500 transition-colors">
                Images
                <span className="normal-case font-normal tracking-normal ml-1">
                  ({totalImageCount}
                  {sharedMax ? `/${sharedMax}` : ''} uploaded)
                </span>
                <CollapsedIssueBadge {...badgeCounts} isOpen={isOpen('images')} />
              </p>
              <ChevronDown
                className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isOpen('images') ? 'rotate-180' : ''}`}
              />
            </button>
            <div className="border-b border-slate-200 mb-3" />
            {isOpen('images') && (
              <div className="space-y-4">
                {sharedMaxIssue?.errors.map((msg, i) => (
                  <p key={`se${i}`} className="text-xs text-red-600">
                    ✕ {msg}
                  </p>
                ))}
                {imagesConditionalIssue?.warnings.map((msg, i) => (
                  <p key={`cw${i}`} className="text-xs text-amber-600">
                    ⚠ {msg}
                  </p>
                ))}
                {imageSlotKeys.map((key) => {
                  const ms = slots[key];
                  const slotIssue = fieldIssues[`media_${ms.slot}`];
                  const thisSlotCount = filesForSlot(ms.slot).filter(
                    (f) => f.file || f.saved,
                  ).length;
                  const effectiveMax = sharedMax
                    ? Math.min(ms.max, sharedMax - totalImageCount + thisSlotCount)
                    : ms.max;
                  const slotCfg = mediaSlotToSlotConfig(ms);
                  if (sharedMax) slotCfg.hideMax = true;
                  return (
                    <div key={ms.slot}>
                      <MediaSlotSection
                        slot={slotCfg}
                        files={filesForSlot(ms.slot)}
                        onAddFiles={(files) => addFiles(ms.slot, files)}
                        maxFiles={effectiveMax}
                        onRemoveFile={handleRemoveFile}
                        issue={slotIssue}
                        onDimensionsLoaded={handleDimensionsLoaded}
                        fieldIssues={fieldIssues}
                        showErrors={showErrors}
                        showWarnings={showWarnings}
                        gxData={gxData}
                        gxLoading={gxLoading}
                        gxError={gxError}
                        campaign={campaign}
                        campaignId={campaignId}
                        readOnly={readOnly}
                      />
                      {slotIssue?.errors.map((msg, i) => (
                        <p key={`e${i}`} className="text-xs text-red-600 mt-1">
                          ✕ {msg}
                        </p>
                      ))}
                      {slotIssue?.warnings.map((msg, i) => (
                        <p key={`w${i}`} className="text-xs text-amber-600 mt-1">
                          ⚠ {msg}
                        </p>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      }

      case 'videos': {
        const videoSlot = slots.video;
        if (!videoSlot) return null;
        const videoIssue = fieldIssues[`media_video`];
        return (
          <div key={index}>
            <button
              type="button"
              onClick={() => toggle('videos')}
              className="flex items-center justify-between w-full mb-1.5 group/sec"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 group-hover/sec:text-slate-500 transition-colors">
                Videos
                <span className="normal-case font-normal tracking-normal ml-1">
                  ({mediaFiles.filter((f) => f.slot === 'video' && (f.file || f.saved)).length}{' '}
                  uploaded)
                </span>
                <CollapsedIssueBadge
                  {...countMediaIssues(fieldIssues, ['video'], mediaFiles)}
                  isOpen={isOpen('videos')}
                />
              </p>
              <ChevronDown
                className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isOpen('videos') ? 'rotate-180' : ''}`}
              />
            </button>
            <div className="border-b border-slate-200 mb-3" />
            {isOpen('videos') && (
              <div>
                <MediaSlotSection
                  slot={mediaSlotToSlotConfig(videoSlot)}
                  files={filesForSlot('video')}
                  onAddFiles={(files) => addFiles('video', files)}
                  maxFiles={videoSlot.max}
                  onRemoveFile={handleRemoveFile}
                  issue={videoIssue}
                  onDimensionsLoaded={handleDimensionsLoaded}
                  fieldIssues={fieldIssues}
                  showErrors={showErrors}
                  showWarnings={showWarnings}
                  gxData={gxData}
                  gxLoading={gxLoading}
                  gxError={gxError}
                  campaign={campaign}
                  campaignId={campaignId}
                  readOnly={readOnly}
                />
                {videoIssue?.errors.map((msg, i) => (
                  <p key={`e${i}`} className="text-xs text-red-600 mt-1">
                    ✕ {msg}
                  </p>
                ))}
                {videoIssue?.warnings.map((msg, i) => (
                  <p key={`w${i}`} className="text-xs text-amber-600 mt-1">
                    ⚠ {msg}
                  </p>
                ))}
              </div>
            )}
          </div>
        );
      }

      case 'logos': {
        const logoSlotKeys = ['square_logo', 'landscape_logo'].filter((k) => k in slots);
        if (logoSlotKeys.length === 0) return null;
        return (
          <div key={index}>
            <button
              type="button"
              onClick={() => toggle('logos')}
              className="flex items-center justify-between w-full mb-1.5 group/sec"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 group-hover/sec:text-slate-500 transition-colors">
                Logos
                <span className="normal-case font-normal tracking-normal ml-1">
                  ({mediaFiles.filter((f) => f.slot.includes('logo') && (f.file || f.saved)).length}{' '}
                  uploaded)
                </span>
                <CollapsedIssueBadge
                  {...countMediaIssues(fieldIssues, logoSlotKeys, mediaFiles)}
                  isOpen={isOpen('logos')}
                />
              </p>
              <ChevronDown
                className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isOpen('logos') ? 'rotate-180' : ''}`}
              />
            </button>
            <div className="border-b border-slate-200 mb-3" />
            {isOpen('logos') && (
              <div className="space-y-4">
                {logoSlotKeys.map((key) => {
                  const ms = slots[key];
                  const slotIssue = fieldIssues[`media_${ms.slot}`];
                  const defaultDef = config.defaultMedia?.[ms.slot];
                  // Env-aware gs:// path from the backend; the "Use Default"
                  // button stays disabled until it has resolved so we never
                  // persist a stale/hardcoded bucket.
                  const defaultStoragePath = defaultMedia?.[ms.slot]?.storagePath;
                  const slotCfg = mediaSlotToSlotConfig(ms);
                  if (defaultDef) slotCfg.defaultMedia = defaultDef;
                  return (
                    <div key={ms.slot}>
                      <MediaSlotSection
                        slot={slotCfg}
                        files={filesForSlot(ms.slot)}
                        onAddFiles={(files) => addFiles(ms.slot, files)}
                        maxFiles={ms.max}
                        onRemoveFile={handleRemoveFile}
                        issue={slotIssue}
                        onDimensionsLoaded={handleDimensionsLoaded}
                        fieldIssues={fieldIssues}
                        showErrors={showErrors}
                        showWarnings={showWarnings}
                        gxData={gxData}
                        gxLoading={gxLoading}
                        gxError={gxError}
                        campaign={campaign}
                        campaignId={campaignId}
                        readOnly={readOnly}
                        onUseDefault={
                          defaultDef && defaultStoragePath
                            ? () => {
                                onMediaFilesChange([
                                  ...mediaFiles,
                                  {
                                    id: makeMediaId(ms.slot),
                                    slot: ms.slot,
                                    file: null,
                                    saved: {
                                      id: `default-${ms.slot}`,
                                      slot: ms.slot,
                                      storage_path: defaultStoragePath,
                                      content_type: defaultDef.contentType,
                                      file_size: 0,
                                      signed_url: `/api/static/${defaultDef.path}`,
                                    },
                                  },
                                ]);
                              }
                            : undefined
                        }
                      />
                      {slotIssue?.errors.map((msg, i) => (
                        <p key={`e${i}`} className="text-xs text-red-600 mt-1">
                          ✕ {msg}
                        </p>
                      ))}
                      {slotIssue?.warnings.map((msg, i) => (
                        <p key={`w${i}`} className="text-xs text-amber-600 mt-1">
                          ⚠ {msg}
                        </p>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      }

      case 'youtube_videos': {
        const ytSlot = slots.youtube_video;
        if (!ytSlot) return null;
        const ytFiles = mediaFiles.filter((f) => f.slot === 'youtube_video');
        return (
          <div key={index}>
            <button
              type="button"
              onClick={() => toggle('youtube_videos')}
              className="flex items-center justify-between w-full mb-1.5 group/sec"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 group-hover/sec:text-slate-500 transition-colors">
                YouTube Videos
                <span className="normal-case font-normal tracking-normal ml-1">
                  ({ytSlot.min}–{ytSlot.max}){ytSlot.minLength ? ` · ${ytSlot.minLength}` : ''}
                  {ytFiles.filter((f) => f.url?.trim()).length > 0 && (
                    <span className="ml-1 text-slate-300 tabular-nums">
                      · {ytFiles.filter((f) => f.url?.trim()).length} added
                    </span>
                  )}
                </span>
                <CollapsedIssueBadge
                  {...countIssuesForPrefix(fieldIssues, 'media_youtube_video')}
                  isOpen={isOpen('youtube_videos')}
                />
              </p>
              <ChevronDown
                className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isOpen('youtube_videos') ? 'rotate-180' : ''}`}
              />
            </button>
            <div className="border-b border-slate-200 mb-3" />
            {isOpen('youtube_videos') &&
              (() => {
                const ytIssue = fieldIssues['media_youtube_video'];
                const hasError = !!(ytIssue?.errors.length && showErrors);
                const hasWarning = !!(ytIssue?.warnings.length && showWarnings);
                const borderColor = hasError
                  ? 'border-red-300'
                  : hasWarning
                    ? 'border-amber-300'
                    : 'border-slate-200';
                return (
                  <fieldset disabled={readOnly}>
                    <div className={`border-l-2 ${borderColor} pl-3 py-1`}>
                      {ytFiles.length > 0 && (
                        <div className="space-y-2 mb-2">
                          {ytFiles.map((item, idx) => (
                            <YouTubeVideoCard
                              key={item.id}
                              item={item}
                              index={idx}
                              onUrlChange={(url) => updateYouTube(item.id, { url })}
                              onExpirationChange={(date) =>
                                updateYouTube(item.id, { expirationDate: date })
                              }
                              canRemove={ytFiles.length > ytSlot.min}
                              onRemove={() => handleRemoveFile(item.id)}
                              issue={fieldIssues[`youtube_video_${idx}`]}
                              expirationIssue={fieldIssues[`youtube_video_${idx}_expiration`]}
                            />
                          ))}
                        </div>
                      )}
                      {ytFiles.length < ytSlot.max && (
                        <button
                          type="button"
                          onClick={addYouTubeUrl}
                          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#0014cc] transition-colors"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Add YouTube Video
                        </button>
                      )}
                    </div>
                    {hasError &&
                      ytIssue!.errors.map((msg, i) => (
                        <p key={`e${i}`} className="text-xs text-red-600 mt-1">
                          ✕ {msg}
                        </p>
                      ))}
                    {hasWarning &&
                      !hasError &&
                      ytIssue!.warnings.map((msg, i) => (
                        <p key={`w${i}`} className="text-xs text-amber-600 mt-1">
                          ⚠ {msg}
                        </p>
                      ))}
                  </fieldset>
                );
              })()}
          </div>
        );
      }

      case 'companion_banner': {
        const bannerSlot = slots.companion_banner;
        if (!bannerSlot) return null;
        return (
          <div key={index}>
            <button
              type="button"
              onClick={() => toggle('companion_banner')}
              className="flex items-center justify-between w-full mb-1.5 group/sec"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 group-hover/sec:text-slate-500 transition-colors">
                Companion Banner
                <span className="normal-case font-normal tracking-normal ml-1">
                  ({filesForSlot('companion_banner').filter((f) => f.file || f.saved).length}{' '}
                  uploaded)
                </span>
                <CollapsedIssueBadge
                  {...countIssuesForKeys(fieldIssues, ['media_companion_banner'])}
                  isOpen={isOpen('companion_banner')}
                />
              </p>
              <ChevronDown
                className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isOpen('companion_banner') ? 'rotate-180' : ''}`}
              />
            </button>
            <div className="border-b border-slate-200 mb-3" />
            {isOpen('companion_banner') && (
              <div>
                <MediaSlotSection
                  slot={mediaSlotToSlotConfig(bannerSlot)}
                  files={filesForSlot('companion_banner')}
                  onAddFiles={(files) => addFiles('companion_banner', files)}
                  maxFiles={bannerSlot.max}
                  onRemoveFile={handleRemoveFile}
                  issue={fieldIssues['media_companion_banner']}
                  onDimensionsLoaded={handleDimensionsLoaded}
                  fieldIssues={fieldIssues}
                  showErrors={showErrors}
                  showWarnings={showWarnings}
                  gxData={gxData}
                  gxLoading={gxLoading}
                  gxError={gxError}
                  campaign={campaign}
                  campaignId={campaignId}
                  readOnly={readOnly}
                />
                {fieldIssues['media_companion_banner']?.errors.map((msg, i) => (
                  <p key={`e${i}`} className="text-xs text-red-600 mt-1">
                    ✕ {msg}
                  </p>
                ))}
                {fieldIssues['media_companion_banner']?.warnings.map((msg, i) => (
                  <p key={`w${i}`} className="text-xs text-amber-600 mt-1">
                    ⚠ {msg}
                  </p>
                ))}
              </div>
            )}
          </div>
        );
      }

      case 'cta':
        if (!config.cta) return null;
        return (
          <fieldset key={index} disabled={readOnly}>
            <SearchCTADropdown
              values={state.cta}
              onChange={(v) => onChange({ cta: v })}
              options={config.cta.options}
              min={config.cta.min}
              max={config.cta.max}
              fieldIssues={fieldIssues}
              showErrors={showErrors}
              showWarnings={showWarnings}
              allowCustom={config.cta.allowCustom}
              customCharLimit={config.cta.customCharLimit}
            />
          </fieldset>
        );

      case 'final_url':
        return (
          <fieldset key={index} disabled={readOnly}>
            <FieldLabel label="Final URL" />
            <input
              type="url"
              value={state.finalUrl}
              onChange={(e) => onChange({ finalUrl: e.target.value })}
              placeholder="https://ford.com/explorer"
              className={`w-full px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 transition-all ${
                issueBorder(fieldIssues['final_url'], showErrors, showWarnings) ||
                'border-slate-300 focus:ring-[#0014cc]/10 focus:border-[#0014cc]/50'
              }`}
            />
            <InlineIssue
              issue={fieldIssues['final_url']}
              showErrors={showErrors}
              showWarnings={showWarnings}
            />
          </fieldset>
        );

      case 'image_extensions':
        if (!onImageExtensionsChange) return null;
        return (
          <div key={index}>
            <RSAImageExtensions
              imageExtensions={imageExtensions}
              onImageExtensionsChange={onImageExtensionsChange}
              campaign={campaign}
              campaignId={campaignId}
              readOnly={readOnly}
              gxData={gxData}
              gxLoading={gxLoading}
              gxError={gxError}
              fieldIssues={fieldIssues}
              showErrors={showErrors}
            />
          </div>
        );

      case 'images_placeholder':
        return (
          <div key={index}>
            <button
              type="button"
              onClick={() => toggle('images_placeholder')}
              className="flex items-center justify-between w-full mb-1.5 group/sec"
            >
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 group-hover/sec:text-slate-500 transition-colors">
                Images
              </p>
              <ChevronDown
                className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isOpen('images_placeholder') ? 'rotate-180' : ''}`}
              />
            </button>
            <div className="border-b border-slate-200 mb-3" />
            {isOpen('images_placeholder') && (
              <div className="rounded-xl border-2 border-dashed border-slate-200 p-4 flex flex-col items-center gap-2 text-center">
                <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                  <ImagePlus className="w-4 h-4 text-slate-400" />
                </div>
                <p className="text-xs text-slate-500 font-medium">
                  Pending requirements clarification
                </p>
                <p className="text-[11px] text-slate-400">
                  Image specs will be added once confirmed
                </p>
              </div>
            )}
          </div>
        );

      case 'sitelinks':
        return (
          <div key={index}>
            <SitelinksSection
              values={state.sitelinks}
              onChange={(v) => onChange({ sitelinks: v })}
              fieldIssues={fieldIssues}
              showErrors={showErrors}
              showWarnings={showWarnings}
              readOnly={readOnly}
            />
          </div>
        );

      case 'legal':
        return (
          <fieldset key={index} disabled={readOnly}>
            <FieldLabel
              label={
                <>
                  Legal{' '}
                  <span className="normal-case font-normal tracking-normal text-slate-400">
                    (optional)
                  </span>
                </>
              }
            />
            <textarea
              value={state.legal}
              onChange={(e) => onChange({ legal: e.target.value })}
              placeholder="Legal disclaimer..."
              rows={3}
              className={`${inputClass} resize-none`}
            />
          </fieldset>
        );

      default:
        return null;
    }
  }

  return (
    <>
      {/* Delete confirmation modal */}
      {pendingDeleteId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-80 bg-white rounded-xl border border-slate-200 shadow-xl flex flex-col overflow-hidden">
            <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl flex-shrink-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Delete File
              </p>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-slate-600">
                Are you sure you want to remove this file? You will still need to press Save.
              </p>
              <div className="flex gap-2 mt-5 justify-end">
                <button
                  onClick={() => setPendingDeleteId(null)}
                  className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => removeFile(pendingDeleteId)}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto hover-scroll px-6 py-5 space-y-6 min-h-0">
        <fieldset disabled={readOnly}>
          <OfferLedSection
            isOfferLed={state.isOfferLed}
            offerEndDate={state.offerEndDate}
            onOfferLedChange={(isOfferLed) =>
              // Clearing the date on toggle-off keeps the client and the backend
              // (which nulls it too) in agreement, so nothing stale comes back.
              onChange({ isOfferLed, ...(isOfferLed ? {} : { offerEndDate: '' }) })
            }
            onOfferEndDateChange={(offerEndDate) => onChange({ offerEndDate })}
            fieldIssues={fieldIssues}
            showErrors={showErrors}
            showWarnings={showWarnings}
          />
        </fieldset>
        {config.sections.map((section, index) => renderSection(section, index))}
      </div>
    </>
  );
}

// ─── RSA Image Extensions — two fixed slots (square + landscape) ────────────

function RSAImageExtensions({
  imageExtensions,
  onImageExtensionsChange,
  campaign,
  campaignId,
  readOnly = false,
  gxData,
  gxLoading,
  gxError,
  fieldIssues = {},
  showErrors = false,
}: {
  imageExtensions: ImageExtension[];
  onImageExtensionsChange: (extensions: ImageExtension[]) => void;
  campaign?: Campaign | null;
  campaignId?: string;
  readOnly?: boolean;
  gxData?: GXMultiSearchResponse | null;
  gxLoading?: boolean;
  gxError?: boolean;
  fieldIssues?: Record<string, FieldIssue>;
  showErrors?: boolean;
}) {
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!previewSrc) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setPreviewSrc(null);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [previewSrc]);
  const [gxSlotIndex, setGxSlotIndex] = useState<number | null>(null);
  const [defaults, setDefaults] = useState<RsaDefaultImageSet | null>(null);
  const [defaultsLoading, setDefaultsLoading] = useState(false);

  useEffect(() => {
    const nameplate = campaign?.nameplate;
    if (!nameplate) {
      setDefaults(null);
      setDefaultsLoading(false);
      return;
    }
    let ignore = false;
    setDefaultsLoading(true);
    fetchRsaDefaults(nameplate)
      .then((result) => {
        if (!ignore) setDefaults(result);
      })
      .finally(() => {
        if (!ignore) setDefaultsLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [campaign?.nameplate]);

  function setSlot(index: number, ext: ImageExtension) {
    const next = [...imageExtensions];
    while (next.length <= index) {
      next.push({
        id: `img-ext-${Date.now()}-${next.length}`,
        file: null,
        saved: null,
        sourceFordshareId: '',
        expirationDate: '',
      });
    }
    next[index] = ext;
    onImageExtensionsChange(next);
  }

  function clearSlot(index: number) {
    const next = [...imageExtensions];
    next[index] = {
      id: `img-ext-${Date.now()}-${index}`,
      file: null,
      saved: null,
      sourceFordshareId: '',
      expirationDate: '',
    };
    onImageExtensionsChange(next);
  }

  function handleUpload(index: number, file: File) {
    setSlot(index, {
      id: `img-ext-${Date.now()}-${index}`,
      file,
      saved: null,
      sourceFordshareId: '',
      expirationDate: imageExtensions[index]?.expirationDate ?? '',
    });
  }

  function handleUseDefaults() {
    if (!defaults) return;
    const next: ImageExtension[] = [
      {
        id: `img-ext-default-square-${Date.now()}`,
        file: null,
        saved: {
          id: 'default-rsa-square',
          slot: 'image_extension',
          storage_path: defaults.square.storagePath,
          content_type: 'image/jpeg',
          file_size: 0,
          signed_url: defaults.square.staticUrl,
        },
        sourceFordshareId: '',
        expirationDate: imageExtensions[0]?.expirationDate ?? '',
      },
      {
        id: `img-ext-default-landscape-${Date.now()}`,
        file: null,
        saved: {
          id: 'default-rsa-landscape',
          slot: 'image_extension',
          storage_path: defaults.landscape.storagePath,
          content_type: 'image/jpeg',
          file_size: 0,
          signed_url: defaults.landscape.staticUrl,
        },
        sourceFordshareId: '',
        expirationDate: imageExtensions[1]?.expirationDate ?? '',
      },
    ];
    onImageExtensionsChange(next);
  }

  function updateExpiration(index: number, date: string) {
    const ext = imageExtensions[index];
    if (ext) {
      setSlot(index, { ...ext, expirationDate: date });
    }
  }

  function handleGxImport(index: number, imported: GXImportedFile[]) {
    if (imported[0]) {
      handleUpload(index, imported[0].file);
    }
    setGxSlotIndex(null);
  }

  function isDefault(ext: ImageExtension | undefined): boolean {
    return !!ext?.saved?.storage_path?.includes('defaults/rsa-images/');
  }

  function getImageSrc(ext: ImageExtension | undefined): string | null {
    if (!ext) return null;
    if (ext.file) return URL.createObjectURL(ext.file);
    return ext.saved?.signed_url ?? null;
  }

  return (
    <>
      {previewSrc &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80"
            onClick={() => setPreviewSrc(null)}
          >
            <button
              type="button"
              aria-label="Close preview"
              onClick={(e) => {
                e.stopPropagation();
                setPreviewSrc(null);
              }}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={previewSrc}
              alt="Full preview"
              className="max-w-[90vw] max-h-[90vh] rounded-lg object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>,
          document.body,
        )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-baseline gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              Image Extensions
            </p>
            <span className="text-[10px] text-slate-300">Optional · 1 square + 1 landscape</span>
          </div>
          {(defaults || defaultsLoading) && !readOnly && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={handleUseDefaults}
                  disabled={defaultsLoading || !defaults}
                  className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-400 hover:text-slate-600 transition-colors underline underline-offset-2 decoration-slate-300 disabled:opacity-50 disabled:cursor-wait"
                >
                  <ImageIcon className="w-3 h-3" />
                  Default Images
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="p-3 max-w-64">
                <div className="space-y-2">
                  <p className="text-xs text-center leading-snug">
                    Adds default images for <strong>{campaign?.nameplate}</strong>. You can replace
                    them anytime.
                  </p>
                  {defaults && (
                    <div className="flex gap-2">
                      <img
                        src={defaults.square.staticUrl}
                        alt="Square default"
                        className="w-16 h-16 rounded object-cover border border-white/10"
                      />
                      <img
                        src={defaults.landscape.staticUrl}
                        alt="Landscape default"
                        className="w-24 h-16 rounded object-cover border border-white/10"
                      />
                    </div>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          {(
            [
              { index: 0, label: 'Square', ratio: '1:1', aspect: '1/1' },
              { index: 1, label: 'Landscape', ratio: '1.91:1', aspect: '1.91/1' },
            ] as const
          ).map(({ index, label, ratio, aspect }) => {
            const ext = imageExtensions[index];
            const src = getImageSrc(ext);
            const hasImage = !!(ext?.file || ext?.saved);
            const isDef = isDefault(ext);

            return (
              <div
                key={label}
                className="rounded-lg border border-slate-200 bg-white p-3 space-y-2.5"
              >
                <div className="flex items-center gap-1.5">
                  <p className="text-[11px] font-semibold text-slate-500">{label}</p>
                  <span className="text-[10px] text-slate-300">{ratio}</span>
                  <div className="ml-auto flex items-center gap-2">
                    {hasImage && isDef && (
                      <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-500 border border-blue-200">
                        Default
                      </span>
                    )}
                    {hasImage && !isDef && (
                      <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                        Custom
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-center" style={{ aspectRatio: '1.91/1' }}>
                  {hasImage && src ? (
                    <div
                      className="relative group/img rounded-lg overflow-hidden h-full"
                      style={{ aspectRatio: aspect }}
                    >
                      <img src={src} alt={label} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        aria-label="Preview image"
                        onClick={() => setPreviewSrc(src)}
                        className="absolute top-1 left-1 w-6 h-6 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center shadow-md opacity-0 group-hover/img:opacity-100 transition-opacity"
                      >
                        <Eye className="w-3 h-3" />
                      </button>
                      {!readOnly && (
                        <button
                          type="button"
                          aria-label="Remove image"
                          onClick={() => clearSlot(index)}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-md opacity-0 group-hover/img:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ) : readOnly ? (
                    <div
                      className="w-full h-full rounded-lg border-2 border-dashed border-slate-200 bg-slate-50/50 flex flex-col items-center justify-center gap-1"
                      style={{ aspectRatio: aspect }}
                    >
                      <p className="text-[10px] text-slate-300">No image</p>
                    </div>
                  ) : (
                    <label className="block cursor-pointer h-full" style={{ aspectRatio: aspect }}>
                      <input
                        type="file"
                        accept="image/jpeg,image/png"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files?.[0]) handleUpload(index, e.target.files[0]);
                          e.target.value = '';
                        }}
                      />
                      <div className="w-full h-full rounded-lg border-2 border-dashed border-slate-200 bg-slate-50/50 hover:border-slate-300 transition-all flex flex-col items-center justify-center gap-1">
                        <Plus className="w-4 h-4 text-slate-300" />
                        <p className="text-[10px] text-slate-400">Upload {ratio}</p>
                      </div>
                    </label>
                  )}
                </div>

                {!readOnly && (
                  <GearExchangeImportButton
                    totalAssets={gxData?.total_assets ?? null}
                    loading={gxLoading}
                    error={gxError}
                    fordshareIds={campaign?.fordshare_entries?.map((e) => e.id)}
                    onClick={() => setGxSlotIndex(index)}
                  />
                )}

                <div>
                  <p className="text-[10px] text-slate-400 mb-1">Expiration</p>
                  {(() => {
                    const expIssue = fieldIssues[`image_extension_${index}_expiration`];
                    const hasError = !!(expIssue?.errors.length && showErrors);
                    return (
                      <>
                        <input
                          type="date"
                          value={ext?.expirationDate ?? ''}
                          onChange={(e) => updateExpiration(index, e.target.value)}
                          disabled={readOnly}
                          className={`w-full px-2.5 py-1.5 text-xs border rounded-lg bg-white focus:outline-none focus:ring-2 transition-all ${
                            hasError
                              ? 'border-red-400 focus:border-red-400 focus:ring-red-100'
                              : 'border-slate-300 focus:ring-[#0014cc]/10 focus:border-[#0014cc]/50'
                          } ${ext?.expirationDate ? 'text-slate-900' : 'text-slate-400'}`}
                        />
                        {hasError &&
                          expIssue!.errors.map((msg, i) => (
                            <p key={i} className="text-[10px] text-red-600 mt-1">
                              ✕ {msg}
                            </p>
                          ))}
                      </>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>

        {gxSlotIndex !== null && gxData && campaign && campaignId && (
          <GearExchangeModal
            data={gxData}
            campaign={campaign}
            campaignId={campaignId}
            formatLabel={gxSlotIndex === 0 ? 'Square Image Extension' : 'Landscape Image Extension'}
            zoneLimits={[{ ratio: 'any', limit: 1, existing: 0 }]}
            onClose={() => setGxSlotIndex(null)}
            onImport={(imported) => handleGxImport(gxSlotIndex, imported)}
            slotFilter={{
              acceptTypes: ['image/jpeg', 'image/png'],
              acceptedRatios: gxSlotIndex === 0 ? [1] : [1.91],
            }}
          />
        )}
      </div>
    </>
  );
}
